import { useContext, useReducer, useState } from 'react';

import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { Mail } from 'lucide-react';

import { COLORS } from '../../../../Constant/COLORS';

import { object, string, ValidationError } from 'yup';

import Axios from '../../../../Assets/Axios/Axios';

import ToastContext from '../../../../Context/ToastContext';

import { TypoPoppins } from '../../../../Components/Typo';
import PhoneNumberField from '../../../../Components/PhoneNumberField';

import { useIsMobile } from '../../../../Hooks/useIsMobile';

import { CheckProfessionalContact } from '../../../../Server/Query';

import { useFormContext } from './FormContext';

function ContactInformation() {
  const { showToastMessage } = useContext(ToastContext);
  const { inputs, updateInputs, otpReferenceId, nextStep } = useFormContext();

  const [isSendBtnDisabled, setIsSendBtnDisabled] = useState(false);
  const [errors, updateErrors] = useReducer(errorsReducer, {
    mobileNumber: null,
    email: null,
  });

  const isMobile = useIsMobile();

  const handleSubmit = async event => {
    try {
      event.preventDefault();
      setIsSendBtnDisabled(true);
      updateErrors({ type: ERROR_ACTIONS.CLEAR_ERRORS });

      const { mobileNumber, email } = await schema.validate(inputs, { abortEarly: false });

      // Public, no-auth duplicate check — only the professional table, no user
      // (event-booker) lookup, so this works before the partner has logged in.
      const { data, errors: checkErrors } = await Axios(
        CheckProfessionalContact({ email, mobileNumber })
      );

      if (checkErrors || !data?.checkProfessionalContact) {
        throw new Error('DB failed');
      }

      const { emailTaken, mobileTaken } = data.checkProfessionalContact;

      if (emailTaken || mobileTaken) {
        if (mobileTaken) {
          updateErrors({
            type: ERROR_ACTIONS.SET_ERROR,
            payload: { name: 'mobileNumber', error: 'Mobile Number already in use' },
          });
        }
        if (emailTaken) {
          updateErrors({
            type: ERROR_ACTIONS.SET_ERROR,
            payload: { name: 'email', error: 'Email already in use' },
          });
        }
        return undefined;
      }

      // OTP is bypassed for now (see CodeVerification — code "0000"); keep the
      // flow identical with a stub reference id.
      otpReferenceId.mobileNumber = 'dev-bypass';

      updateInputs({ email, mobileNumber });
      nextStep();
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach(err =>
          updateErrors({
            type: ERROR_ACTIONS.SET_ERROR,
            payload: { name: err.path, error: err.message },
          })
        );
        return undefined;
      }
      showToastMessage('Something went wrong');
    } finally {
      setIsSendBtnDisabled(false);
    }
  };

  function handleChange(event) {
    const { value, name } = event.target;
    updateInputs({ [name]: value });
  }

  return (
    <Stack
      boxSizing='border-box'
      component='form'
      alignItems='center'
      width='min(100%,444px)'
      mx='auto'
      my={8}
      px={2}
    onSubmit={handleSubmit}>
      <Typography
        textAlign='center'
        variant='h4'
        mb={1}
        variantMapping={{ h4: 'h1' }}>
        Create your partner account
      </Typography>
      <TypoPoppins
        textAlign='center'
        color='text.secondary'
        variant={isMobile ? 'caption' : 'body2'}
        display='block'>
        Create an account to list and manage your account.
      </TypoPoppins>
      <Stack width='100%' my={{ xs: 3, md: 5 }} gap={2} alignItems='center' textAlign='center'>
        <PhoneNumberField
          fullWidth
          name='mobileNumber'
          placeholder='Mobile Number'
          // The field already shows a "+94" prefix. Strip a stored country code so
          // a pre-filled value (e.g. after "Edit Details") shows the local number
          // instead of "+94+94…"; the submit transform re-normalizes it anyway.
          value={(inputs.mobileNumber || '').replace(/^\+94/, '')}
          error={Boolean(errors.mobileNumber)}
          helperText={errors.mobileNumber}
          onChange={handleChange}
        />
        <TextField
          inputProps={{ spellCheck: false }}
          autoComplete='email'
          fullWidth
          name='email'
          placeholder='Email'
          value={inputs.email}
          error={Boolean(errors.email)}
          helperText={errors.email}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Mail size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Button color='blue' type='submit' disabled={isSendBtnDisabled}>
        Continue
      </Button>
    </Stack>
  );
}

const ERROR_ACTIONS = Object.freeze({
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_ERROR: 'SET_ERROR',
});

function errorsReducer(state, { payload, type }) {
  switch (type) {
    case ERROR_ACTIONS.SET_ERROR:
      return { ...state, [payload.name]: payload.error };
    case ERROR_ACTIONS.CLEAR_ERRORS:
      const fields = Object.fromEntries(Object.keys({ ...state }).map(field => [field, null]));
      return fields;
    default:
      return state;
  }
}

const schema = object({
  mobileNumber: string()
    .trim()
    .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{3,6}$/im, {
      message: 'Mobile Number is invalid',
    })
    .min(9, 'Mobile Number is invalid')
    .max(12, 'Mobile Number is invalid')
    .transform(value => `+94${value.slice(-9)}`),
  email: string().email().required().label('Email'),
});

export default ContactInformation;
